import { Input } from "@/components/ui/input";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { sendResetPasswordEmail, verifyResetPasswordCode, confirmResetPassword } from "@/firebase";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { CheckCircle2, Loader2 } from "lucide-react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import PasswordInput from "@/components/ui/password-input";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { internationalEmail } from "@/utils/form";

// const isDevelopment = import.meta.env.VITE_APP_ENV === "dev";

/**
 * Creates the schema for the reset password email form validation
 * Uses international email validation to support German umlauts (ä, ö, ü, ß)
 *
 * @param t - Translation function for error messages
 * @returns Zod schema object for email form validation
 */
const createResetPasswordEmailSchema = (t: (_key: string) => string) => {
  return z.object({
    email: internationalEmail(t("resetpassword.validation.emailinvalid")),
  });
};

/**
 * Creates the schema for the new password form validation
 *
 * @param t - Translation function for error messages
 * @returns Zod schema object for password form validation
 */
const createNewPasswordSchema = (t: (_key: string) => string) => {
  return z.object({
    newPassword: z
      .string()
      .min(8, t("resetpassword.validation.passwordtooshort")),
    confirmPassword: z
      .string()
      .min(1, t("resetpassword.validation.confirmpasswordrequired"))
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: t("resetpassword.validation.passwordmismatch"),
    path: ["confirmPassword"],
  });
};

type ResetPasswordEmailFormData = z.infer<ReturnType<typeof createResetPasswordEmailSchema>>;
type NewPasswordFormData = z.infer<ReturnType<typeof createNewPasswordSchema>>;

const ResetPassword = () => {

  const { t } = useTranslation(["translation", "toasts"]);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [cooldownMsRemaining, setCooldownMsRemaining] = useState<number>(0);
  const [tokenVerifying, setTokenVerifying] = useState<boolean>(false);
  const [tokenValid, setTokenValid] = useState<boolean>(false);
  const [resetting, setResetting] = useState<boolean>(false);
  const [resetSuccess, setResetSuccess] = useState<boolean>(false);
  const [resetError, setResetError] = useState<string>("");

  // Form setup with Zod validation
  const emailSchema = createResetPasswordEmailSchema(t);
  const passwordSchema = createNewPasswordSchema(t);

  const emailForm = useForm<ResetPasswordEmailFormData>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: "",
    },
  });

  const passwordForm = useForm<NewPasswordFormData>({
    resolver: zodResolver(passwordSchema),
    defaultValues: {
      newPassword: "",
      confirmPassword: "",
    },
  });

  const navigate = useNavigate();
  const { token } = useParams();
  const [searchParams] = useSearchParams();

  const COOLDOWN_MS = 60_000;
  const STORAGE_PREFIX = "reset_password_last_sent_ts:";
  const STORAGE_LAST_EMAIL = "reset_password_last_email";

  const emailValue = emailForm.watch("email");
  const normalizedEmail = useMemo(() => emailValue.trim().toLowerCase(), [emailValue]);
  const storageKey = useMemo(() => `${STORAGE_PREFIX}${normalizedEmail || "__empty__"}`, [normalizedEmail]);
  const canSend = useMemo(() => !isLoading && cooldownMsRemaining <= 0 && normalizedEmail.length > 0 && emailForm.formState.isValid, [isLoading, cooldownMsRemaining, normalizedEmail.length, emailForm.formState.isValid]);
  const cooldownSeconds = Math.max(0, Math.ceil(cooldownMsRemaining / 1000));

  useEffect(() => {
    // Restore last used email from sessionStorage so cooldown can resume on refresh
    const savedEmail = sessionStorage.getItem(STORAGE_LAST_EMAIL);
    if (savedEmail) {
      emailForm.setValue("email", savedEmail);
    }
  }, [emailForm]);

  useEffect(() => {
    if (!normalizedEmail) {
      setCooldownMsRemaining(0);
      setIsSuccess(false);
      return;
    }
    const stored = localStorage.getItem(storageKey);
    const lastSentTs = stored ? Number(stored) : 0;
    if (Number.isFinite(lastSentTs) && lastSentTs > 0) {
      const remaining = Math.max(0, lastSentTs + COOLDOWN_MS - Date.now());
      setCooldownMsRemaining(remaining);
      // Only show success message if there's an active timeout
      setIsSuccess(remaining > 0);
    } else {
      setCooldownMsRemaining(0);
      setIsSuccess(false);
    }
  }, [storageKey, normalizedEmail]);

  useEffect(() => {
    if (cooldownMsRemaining <= 0) return;
    const interval = setInterval(() => {
      setCooldownMsRemaining((prev) => Math.max(0, prev - 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldownMsRemaining]);

  useEffect(() => {
    // When cooldown ends, allow editing again and clear the saved email for this tab session
    if (cooldownMsRemaining <= 0) {
      sessionStorage.removeItem(STORAGE_LAST_EMAIL);
      setIsSuccess(false); // Hide success message when timeout expires
    }
  }, [cooldownMsRemaining]);

  // When coming from Firebase email link (?mode=resetPassword&oobCode=...), route to /reset-password/:token
  useEffect(() => {
    const mode = searchParams.get("mode");
    const oobCode = searchParams.get("oobCode");
    if (!token && mode === "resetPassword" && oobCode) {
      navigate(`/reset-password/${oobCode}`, { replace: true });
    }
  }, [searchParams, token, navigate]);

  // Verify token if present
  useEffect(() => {
    const verify = async () => {
      if (!token) return;
      setTokenVerifying(true);
      setResetError("");
      try {
        await verifyResetPasswordCode(token);
        setTokenValid(true);
      } catch (_e) {
        setTokenValid(false);
        setResetError(t("resetpassword.invalidlink"));
      } finally {
        setTokenVerifying(false);
      }
    };
    verify();
  }, [token, t]);

  const handleSendResetPasswordEmail = async (data: ResetPasswordEmailFormData) => {
    if (!canSend) return;
    setIsLoading(true);
    const email = data.email.trim().toLowerCase();
    try {
      await sendResetPasswordEmail(email);
      setIsSuccess(true);
      const now = Date.now();
      localStorage.setItem(storageKey, String(now));
      sessionStorage.setItem(STORAGE_LAST_EMAIL, email);
      setCooldownMsRemaining(COOLDOWN_MS);
    } catch (_e) {
      // Intentionally do not reveal whether the email exists to avoid enumeration
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReset = async (data: NewPasswordFormData) => {
    if (!token) return;
    setResetting(true);
    setResetError("");
    try {
      await confirmResetPassword(token, data.newPassword);
      setResetSuccess(true);
    } catch (_e) {
      setResetError(t("resetpassword.unabletoreset"));
    } finally {
      setResetting(false);
    }
  };

  return (
    <div className="w-full max-w-md p-8">
      <div className="flex flex-col gap-2 text-center mb-6">
        <h1 className="text-2xl font-bold">{t("resetpassword.title")}</h1>
        <p className="text-sm text-slate-600">{t("resetpassword.description")}</p>
      </div>
      {/* Token-based reset form (available in all envs) */}
      {token && (
        <div className="w-full space-y-3 mb-6">
          {tokenVerifying && (
            <div className="flex items-center text-gray-700"><Loader2 className="mr-2 h-4 w-4 animate-spin" />{t("resetpassword.verifyinglink")}</div>
          )}
          {!tokenVerifying && !tokenValid && (
            <div className="rounded-md border border-red-200 bg-red-50 p-3 text-red-800">{resetError || t("resetpassword.invalidlink")}</div>
          )}
          {!tokenVerifying && tokenValid && !resetSuccess && (
            <form onSubmit={passwordForm.handleSubmit(handleConfirmReset)} className="space-y-3">
              <div>
                <PasswordInput
                  placeholder={t("resetpassword.newpassword")}
                  autoComplete="new-password"
                  className="px-4 py-2 rounded-md"
                  {...passwordForm.register("newPassword")}
                />
                {passwordForm.formState.errors.newPassword && (
                  <p className="text-sm text-destructive mt-1">{passwordForm.formState.errors.newPassword.message}</p>
                )}
              </div>
              <div>
                <PasswordInput
                  hideStrengthUI
                  placeholder={t("resetpassword.confirmpassword")}
                  autoComplete="new-password"
                  className="px-4 py-2 rounded-md"
                  {...passwordForm.register("confirmPassword")}
                />
                {passwordForm.formState.errors.confirmPassword && (
                  <p className="text-sm text-destructive mt-1">{passwordForm.formState.errors.confirmPassword.message}</p>
                )}
              </div>
              <Button
                type="submit"
                className="w-full transition-all duration-300"
                disabled={!passwordForm.formState.isValid || resetting}
              >
                {resetting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {resetting ? t("resetpassword.reseting") : t("resetpassword.setnewpassword")}
              </Button>
            </form>
          )}
          {!tokenVerifying && tokenValid && resetSuccess && (
            <div className="flex items-start gap-3 rounded-md border border-green-200 bg-green-50 p-3 text-green-800">
              <CheckCircle2 className="h-5 w-5 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">{t("resetpassword.passwordresetsuccess")}</p>
                <a href="/login" className="text-sm underline">{t("resetpassword.backtologin")}</a>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Email form - only show when no token is present */}
      {!token && (
        <div
          data-cy="resetpassword-email-container"
          className="w-full space-y-2 mb-4"
        >
          {isSuccess && (
            <div className="flex items-start gap-3 rounded-md border border-green-200 bg-green-50 p-3 text-green-800">
              <CheckCircle2 className="h-5 w-5 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium">{t("resetpassword.emailsent")}</p>
                {cooldownSeconds > 0 ? (
                  <p className="text-sm opacity-90">{t("resetpassword.resendin", { seconds: cooldownSeconds })}</p>
                ) : (
                  <p className="text-sm opacity-90">{t("resetpassword.canyouresend")}</p>
                )}
              </div>
            </div>
          )}
          <form onSubmit={emailForm.handleSubmit(handleSendResetPasswordEmail)} className="space-y-2">
            <div>
              <Input
                type="email"
                placeholder={t("resetpassword.email")}
                autoComplete="email"
                className="px-4 py-2 rounded-md"
                disabled={cooldownSeconds > 0}
                {...emailForm.register("email")}
              />
              {emailForm.formState.errors.email && (
                <p className="text-sm text-destructive mt-1">{emailForm.formState.errors.email.message}</p>
              )}
            </div>
            <Button
              type="submit"
              className="w-full transition-all duration-300"
              disabled={!canSend}
            >
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {cooldownSeconds > 0
                ? t("resetpassword.resendin", { seconds: cooldownSeconds })
                : isLoading
                  ? t("resetpassword.sending")
                  : t("resetpassword.sendresetpasswordemail")}
            </Button>
          </form>
        </div>
      )}
      <Separator className="my-4" />

      <div className="flex items-center justify-between text-sm gap-2">
        <p className=" text-gray-800 font-medium">
          {t("resetpassword.alreadyhaveaccount")}
        </p>
        <a onClick={() => { navigate("/login"); }} className="text-blue-400 cursor-pointer">
          {t("login.signin")}
        </a>
      </div>
    </div>
  );
};

export default ResetPassword;
